const DEEP_LINK_STORAGE_KEY = "canucci.deepLink";

export const getStoredDeepLink = (): string | null => {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(DEEP_LINK_STORAGE_KEY);
};

export const setStoredDeepLink = (url: string | null): void => {
  if (typeof window === "undefined") return;
  if (url === null) {
    localStorage.removeItem(DEEP_LINK_STORAGE_KEY);
  } else {
    localStorage.setItem(DEEP_LINK_STORAGE_KEY, url);
  }
};

export const hasReachedDeepLink = (pathname: string, search: string, deepLink: string): boolean =>
  `${pathname}${search}` === deepLink;

/** Same-origin relative path only: starts with `/`, not `//`. */
export const isValidRelativePath = (value: string | null | undefined): value is string =>
  typeof value === "string" && value.startsWith("/") && !value.startsWith("//");

/** Must match `return_to` on `/auth/request-verification-email/` (`auth_mail_router.py`). */
const AUTH_RETURN_PATH_MAX_LENGTH = 4096;

/**
 * The deep link if the auth mail endpoint will accept it as `return_to`, else `undefined`.
 *
 * Mirrors `validate_auth_return_path` in `auth_action_urls.py`, which checks the *decoded* path:
 * an encoded backslash or control character in a query string passes `isValidRelativePath` but is
 * refused there, and that refusal fails the whole request rather than just dropping the link.
 */
export const toAuthReturnPath = (value: string | null | undefined): string | undefined => {
  if (!value || value.length > AUTH_RETURN_PATH_MAX_LENGTH) return undefined;
  let decoded: string;
  try {
    decoded = decodeURIComponent(value);
  } catch {
    return undefined;
  }
  if (!isValidRelativePath(decoded) || decoded.includes("\\")) return undefined;
  for (const ch of decoded) {
    const code = ch.charCodeAt(0);
    if (code < 32 || code === 127) return undefined;
  }
  return value;
};

/**
 * Signs out while keeping a deliberately preserved deep link, and reports what was preserved.
 *
 * The ordering matters and is easy to get wrong. Firebase emits its signed-out event *during*
 * `signOut()`, and the auth-state listener consumes the "preserve this" marker when it does. So
 * whether a deep link should survive cannot be re-read from that marker after the await — by then
 * the listener may already have cleared it, and sign-out would wipe the link the listener had
 * just written. The caller's intent is captured once, up front, and nothing reads it back.
 */
export const signOutPreservingDeepLink = async ({
  signOut,
  preserveDeepLink,
  markPreserved,
  writeStoredDeepLink = setStoredDeepLink,
}: {
  signOut: () => Promise<void>;
  preserveDeepLink: string | null | undefined;
  markPreserved: (url: string | null) => void;
  writeStoredDeepLink?: (url: string | null) => void;
}): Promise<string | null> => {
  const preserved = isValidRelativePath(preserveDeepLink) ? preserveDeepLink : null;
  if (preserved) {
    markPreserved(preserved);
    writeStoredDeepLink(preserved);
  }

  await signOut();

  if (!preserved) {
    writeStoredDeepLink(null);
  }
  return preserved;
};
